import { PropTypes } from 'base/react-libs';
import { useOpenClose } from '@evertrue/et-components';
import { useEffect, useState } from 'react';
import StepRow from 'apps/journeys/components/detail/journey-detail-table/step-view/step-row/step-row';
import JourneyTaskModal from 'apps/journeys/components/modals/journey-task-modal';
import EmptyStateMessage from 'components/lists/empty-state-message';
import AddJourneyTaskButton from 'apps/journeys/components/top-nav/add-journey-task-button';
import { getLinkUrl } from 'entities/helpers/hardcodedLinks';

const StepsTable = ({
  journey = {},
  stepEmpty,
  userIsCreator = false,
  openContactSummary = () => {},
  optionalStateManagementFunction = () => {},
  viewSideBar,
  allSelectedTasks,
  setAllSelectedTasks,
}) => {
  const emptySubtext = (
    <div className="journey-empty-state-message">
      <strong className="journey-empty-state-message-title">Next steps to make it impactful:</strong>
      <ul>
        <li className="journey-empty-state-message-list">
          Think about the frequency of your outreach to ensure that your organization remains top of mind for potential
          donors.
        </li>
        <li className="journey-empty-state-message-list">
          Adds steps to guide your engagement. Remember that you can add steps for preparatory or internal work to help
          keep you on track!
        </li>
        <li className="journey-empty-state-message-list">Add descriptions to clarity actions for your team.</li>
      </ul>
      <p>
        For inspiration and proven strategies, check out the latest articles on the{' '}
        <a href={getLinkUrl('everTrue', 'blogUrl')} target="_blank" rel="noreferrer">
          EverTrue blog.
        </a>
      </p>
    </div>
  );
  const { steps: journeySteps } = journey;
  const [isOpen, openModal, closeModal] = useOpenClose();

  const generateEmptyStateMessage = (stepEmpty) => {
    if (stepEmpty) {
      return 'There are no Cadence Steps that meet the filtered criteria.';
    }
    if (Object.keys(journey).length && !(journeySteps && journeySteps.length)) {
      return 'This Cadence does not have any steps yet.';
    } else return 'Please select a Cadence.';
  };

  //Groups journey steps by day number & asign to state
  // takes in [ {day_number: 1, ...}, {day_number: 1, ...}, {day_number: 2, ...}]
  // returns {1: [{day_number: 1, ...}, {day_number: 1, ...}], 2: [{day_number: 2, ...}]
  const [groupedSteps, setGroupedSteps] = useState([]);
  useEffect(() => {
    if (journeySteps) {
      const arrangedData = {};
      journeySteps.forEach((step) => {
        const { day_number } = step;
        if (!arrangedData[day_number]) {
          arrangedData[day_number] = [];
        }
        arrangedData[day_number].push(step);
      });
      setGroupedSteps(arrangedData);
    }
  }, [journeySteps]);

  return (
    <>
      {Object.keys(journey).length && journeySteps && journeySteps.length ? (
        <>
          {groupedSteps &&
            Object.entries(groupedSteps).map(([day, steps], i) => (
              <div key={`day-${day}`}>
                <div className="journeys-task-table-row--header">Day {day}</div>
                {steps &&
                  steps.map((step, subIndex) => (
                    <StepRow
                      key={`task-${step.id}`}
                      step={step}
                      journey={journey}
                      userIsCreator={userIsCreator}
                      isFinalStep={!!(i === Object.entries(groupedSteps).length - 1) && subIndex === steps.length - 1} // final step of final day
                      openContactSummary={openContactSummary}
                      optionalStateManagementFunction={optionalStateManagementFunction}
                      viewSideBar={viewSideBar}
                      allSelectedTasks={allSelectedTasks}
                      setAllSelectedTasks={setAllSelectedTasks}
                    />
                  ))}
              </div>
            ))}
          {userIsCreator && (
            <AddJourneyTaskButton journey={journey} optionalStateManagementFunction={optionalStateManagementFunction} />
          )}
        </>
      ) : (
        <>
          {userIsCreator && !stepEmpty ? (
            <>
              <EmptyStateMessage
                className="journeys-task-table--empty-state"
                iconSize={0}
                icon="cadences"
                iconKit="gt2"
                text="You’ve successfully created a Cadence!"
                actionLabel="Add Step"
                children={emptySubtext}
                onAction={isOpen ? closeModal : openModal}
              >
                {emptySubtext}
              </EmptyStateMessage>
              <JourneyTaskModal
                isOpen={isOpen}
                onClose={closeModal}
                journey={journey}
                optionalStateManagementFunction={optionalStateManagementFunction}
              />
            </>
          ) : (
            <EmptyStateMessage size="page" text={generateEmptyStateMessage(stepEmpty)} />
          )}
        </>
      )}
    </>
  );
};

StepsTable.propTypes = {
  journey: PropTypes.object.isRequired,
  stepEmpty: PropTypes.bool,
  openContactSummary: PropTypes.func,
  userIsCreator: PropTypes.bool,
  optionalStateManagementFunction: PropTypes.func,
  viewSideBar: PropTypes.bool,
  allSelectedTasks: PropTypes.array,
  setAllSelectedTasks: PropTypes.func,
};

export default StepsTable;
